import React, { ReactElement } from "react";
import { useApolloClient } from "@apollo/client";
import {
    Company, SearchOrganizationsDocument, SearchOrganizationsQuery, useSearchOrganizationsQuery,
} from "../../../__generated__/api";
import { Option, SelectInstanceHandler } from "../../select/Select";
import Select from "../../select/AsyncSelect";

export interface OrganizationPickerProps<TIsMulti = false, TIsClearable=false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    className?: string;
    isClearable?: TIsClearable;
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (sourceId: (TIsMulti extends true ? string[] : string) | (TIsClearable extends true ? undefined : never)) => void;
}

const OrganizationPicker = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, className, isClearable,
}: OrganizationPickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const client = useApolloClient();

    let ids: string[] | undefined;
    if (value) {
        ids = Array.isArray(value) ? value : [value];
    }
    const hasIds = ids?.isNotEmpty ?? false;
    const { data, loading, error } = useSearchOrganizationsQuery({
        variables: {
            ids,
            hasIds,
            limit: 50,
            start: 0,
        },
    });

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<Company>[] : Option<Company>;
    type ValueType = TIsMulti extends true ? Option<Company>[] : Option<Company>;

    const handleSelected = (selected: SelectedType) => {
        if (!selected) {
            (onSelected as (empty: unknown) => void)(undefined);
            return;
        }
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<Company>).value) as Parameters<typeof onSelected>[0];
        onSelected(values);
    };

    const all = (data?.companyResults?.results ?? [])
        .concat(data?.companies.results ?? [])
        .dedupe(row => row.id)
        .sortBy(row => row.name);
    const values = all.map(organization => ({
        label: `${organization.name} (${organization.domain})`,
        value: organization.id.toString() as string,
        data: organization as Company,
    })) ?? [];

    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    const loadOptions = async (query: string) => {
        const searchResults = query ? (await client.query<SearchOrganizationsQuery>({
            query: SearchOrganizationsDocument,
            variables: {
                query,
                limit: 50,
                hasIds: false,
            },
        })).data : data;

        const allResults = (searchResults?.companyResults?.results ?? [])
            .concat(searchResults?.companies.results ?? [])
            .dedupe(row => row.id)
            .sortBy(row => row.name);
        const resultValues = allResults.map(company => ({
            label: `${company.name} (${company.domain})`,
            value: company.id as string,
            data: company,
        })) ?? [];

        return resultValues;
    };

    return (
        <Select
            ref={ref}
            minRequired={minRequired}
            placeholder={placeholder}
            loadOptions={loadOptions}
            isMulti={isMulti}
            onChange={handleSelected}
            value={selected as ValueType}
            className={className}
            isClearable={isClearable}

        />
    );
});

OrganizationPicker.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    className: undefined,
    isClearable: undefined,
};

export default OrganizationPicker as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false
> (
    props: OrganizationPickerProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof OrganizationPicker>;
